'use client'

import { useEffect, useState } from 'react'

import { firstNameOf } from '@/lib/name'
import { GapAnswerCard, PixelBurst, QualityFlagsPanel } from './PrdIntakeChat'

// Founder self-serve edit-and-resubmit screen (PRD intake reset, Task 2).
// Renders once every gap is answered AND the intake is still answerable and
// unlocked (page.tsx's routing — see isAnswerableIntake) — including a
// COMPLETE intake, which is the whole point of the live-until-locked model:
// the founder can come back, change an answer, and resubmit for a
// round-scoped redraft any time before the team locks the draft (share
// auto-locks, or an admin locks explicitly).
//
// Reuses PrdIntakeChat's editable-answer interaction (GapAnswerCard) and
// completion flourish (PixelBurst) rather than re-implementing either — the
// two screens are visually one house language, just different shells around
// the same "gaps" data.
//
// The 'close' affordance is CLIENT-ONLY dismissal: it never calls the API.
// Nothing on this component writes except the two explicit paths (revise a
// single answer, resubmit) — both of which are founder-initiated POSTs to
// the same token-authed turn endpoint the chat already uses.
export function PrdIntakeReview({ token, founderName, initialGaps }) {
  const first = firstNameOf(founderName)
  const [gaps, setGaps] = useState(Array.isArray(initialGaps) ? initialGaps : [])
  const [editingIndex, setEditingIndex] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // null | 'changed' | 'unchanged' — set after a resubmit response, cleared
  // the moment the founder starts editing again (a stale "you're all set"
  // banner sitting over a freshly-edited answer would be actively misleading).
  const [resubmitResult, setResubmitResult] = useState(null)
  const [closed, setClosed] = useState(false)
  const [bannerVisible, setBannerVisible] = useState(false)
  // Quality gate state (T3) — same contract as PrdIntakeChat's: a 422's
  // `qualityFlags` populates this, `overrides` tracks which flagged
  // gapIndexes the founder has toggled "use as-is" for the current attempt.
  // Any successful edit clears both so the next resubmit re-checks fresh.
  const [qualityFlags, setQualityFlags] = useState(null)
  const [overrides, setOverrides] = useState(() => new Set())

  function clearQualityGate() {
    setQualityFlags(null)
    setOverrides(new Set())
  }

  function toggleOverride(index) {
    setOverrides((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  useEffect(() => {
    if (!resubmitResult) return
    setBannerVisible(false)
    const raf = requestAnimationFrame(() => setBannerVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [resubmitResult])

  // Same identity-pinned protocol PrdIntakeChat uses: every write carries
  // the questionText this tab is looking at, so the server can pin the
  // atomic update to (gapIndex, questionText) rather than trusting a numeric
  // index alone — see src/lib/prd/intake.ts's writeGapAnswer for why.
  // `data.reload` means the gap set changed under this tab (e.g. an admin
  // regenerated while it sat open) — the graceful recovery is a full reload,
  // which re-runs the page's server component with a fresh gap set.
  async function saveEdit(index, answerText) {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/z2h/prd/intake/turn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          action: 'revise',
          gapIndex: index,
          questionText: gaps[index]?.question || '',
          answer: answerText,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        if (data.reload) {
          window.location.reload()
          return
        }
        throw new Error(data.error || 'Something went wrong')
      }
      setGaps((g) => {
        const copy = [...g]
        copy[index] = { ...copy[index], answer: answerText }
        return copy
      })
      setEditingIndex(null)
      setResubmitResult(null)
      clearQualityGate()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // Quality gate (T3): resubmit hits the exact same server-side gate as
  // PrdIntakeChat's confirm — a 422 with `qualityFlags` means stop (no
  // completion happened) and show the same nudge-panel UI instead of
  // throwing. Retrying re-sends whatever gapIndexes are currently toggled
  // "use as-is" in `overrideIndexes`.
  async function resubmit(overrideIndexes = []) {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/z2h/prd/intake/turn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          action: 'confirm',
          ...(overrideIndexes.length ? { overrides: overrideIndexes } : {}),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 422 && Array.isArray(data.qualityFlags)) {
        // Minor (a) fix: a 422 means this resubmit did NOT go through — a
        // stale "Sent —" banner from a PRIOR successful resubmit must not
        // keep rendering above the quality-flags panel, which would read as
        // "sent" and "here's what's wrong" contradicting each other.
        setResubmitResult(null)
        setQualityFlags(data.qualityFlags)
        return
      }
      if (!res.ok || !data.ok) {
        if (data.reload) {
          window.location.reload()
          return
        }
        throw new Error(data.error || 'Something went wrong')
      }
      setQualityFlags(null)
      setResubmitResult(data.unchanged ? 'unchanged' : 'changed')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // Purely local dismissal — no fetch, no navigation, no write of any kind.
  function close() {
    setClosed(true)
  }

  if (closed) {
    return (
      <div className="mx-auto mt-10 w-full max-w-md">
        <div className="rounded-3xl border border-kingdom-gold/30 bg-kingdom-gold/[0.06] p-8 text-center">
          <div className="flex justify-center pt-4 pb-8">
            <PixelBurst />
          </div>
          <h2 className="font-display text-2xl font-medium">
            Sounds good{first ? `, ${first}` : ''}.
          </h2>
          <p className="mt-3 text-sm leading-6 text-white/70">
            Your answers are exactly as you left them. Come back to this link any time before
            our team locks the draft in.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-6 w-full">
      <div
        className={
          resubmitResult
            ? `mb-5 rounded-2xl border px-4 py-3 text-center text-sm leading-relaxed transition-opacity duration-500 motion-reduce:transition-none ${
                bannerVisible ? 'opacity-100' : 'opacity-0'
              } ${
                resubmitResult === 'unchanged'
                  ? 'border-white/10 bg-white/[0.03] text-neutral-300'
                  : 'border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-200'
              }`
            : 'hidden'
        }
        role="status"
      >
        {resubmitResult === 'unchanged'
          ? "You're already up to date — nothing new to send."
          : 'Sent — your team will see your updates.'}
      </div>

      <div className="space-y-4">
        {gaps.map((gap, i) =>
          editingIndex === i ? (
            <div key={i} className="flex justify-start">
              <GapAnswerCard
                gap={gap}
                busy={busy}
                initialValue={gap.answer || ''}
                onCancel={() => setEditingIndex(null)}
                onSubmit={(val) => saveEdit(i, val)}
              />
            </div>
          ) : (
            <div
              key={i}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
            >
              <p className="text-sm leading-relaxed text-neutral-100">{gap.question}</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => setEditingIndex(i)}
                title="Tap to change your answer"
                className="mt-3 block w-full rounded-xl border border-kingdom-gold/25 bg-kingdom-gold/[0.05] px-4 py-3 text-left text-sm leading-relaxed text-kingdom-gold transition hover:border-kingdom-gold/50 hover:bg-kingdom-gold/[0.08] disabled:opacity-60"
              >
                {gap.answer}
              </button>
            </div>
          ),
        )}
      </div>

      {error && <p className="mt-4 text-center text-sm text-red-400">{error}</p>}

      {qualityFlags && qualityFlags.length > 0 ? (
        <div className="mt-8">
          <QualityFlagsPanel
            flags={qualityFlags}
            overrides={overrides}
            busy={busy}
            retryLabel="Resubmit"
            onToggleOverride={toggleOverride}
            onEdit={(idx) => {
              setQualityFlags(null)
              setEditingIndex(idx)
            }}
            onRetry={() => resubmit([...overrides])}
          />
        </div>
      ) : (
        <div className="mt-8 space-y-3 text-center">
          <button
            type="button"
            disabled={busy}
            onClick={() => resubmit()}
            className="w-full rounded-full bg-kingdom-gold px-6 py-3.5 font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-60 sm:w-auto sm:px-10"
          >
            {busy ? 'Sending…' : 'Resubmit'}
          </button>
          <p className="text-xs text-neutral-500">Your team will see your updates.</p>
          <button
            type="button"
            disabled={busy}
            onClick={close}
            className="text-sm text-neutral-500 underline decoration-neutral-700 underline-offset-4 transition hover:text-neutral-300 disabled:opacity-60"
          >
            Everything&apos;s right — close
          </button>
        </div>
      )}
    </div>
  )
}
